import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SettingsSection, StatusText } from "../app/SettingsSection";
import { useSession } from "../app/session";
import { useCoreDoc } from "../app/useCoreDoc";
import { useDocSelector } from "../core/useDocSelector";
import { Button, Checkbox, Field, Input, Select, Textarea, ConfirmButton } from "../ui";
import { mailCall, type Account, type Rule, type TestResult } from "./api";
import { RulesEditor } from "./RulesEditor";

type Draft = Partial<Account> & { password?: string };

const EMPTY: Draft = {
  name: "",
  host: "",
  port: 993,
  security: "ssl",
  username: "",
  password: "",
  folder: "INBOX",
  doneFolder: "Drang",
  to: "task",
  allowed: [],
  requireAuth: true,
  mode: "first",
};

function useErrorText() {
  const { t } = useTranslation();
  return (code: string) => t(`errors.${code}`, { defaultValue: t("error.generic") });
}

/** Where the mail goes, the connection and who may send — for a new account or an existing one. */
function AccountForm({
  initial,
  workspaces,
  onSaved,
  onCancel,
}: {
  initial: Draft;
  workspaces: { id: string; name: string }[];
  onSaved(a: Account): void;
  onCancel?(): void;
}) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const [d, setD] = useState<Draft>({ ...initial, workspace: initial.workspace ?? workspaces[0]?.id });
  const [allowed, setAllowed] = useState((initial.allowed ?? []).join("\n"));
  const [error, setError] = useState<string | null>(null);
  const editing = typeof initial.id === "number";
  const set = (patch: Draft) => setD({ ...d, ...patch });
  const save = async () => {
    const body: Record<string, unknown> = {
      name: d.name,
      host: d.host,
      port: d.port,
      security: d.security,
      username: d.username,
      folder: d.folder,
      doneFolder: d.doneFolder,
      workspace: d.workspace,
      to: d.to,
      allowed: allowed.split(/\s*[\n,]\s*/).filter(Boolean),
      requireAuth: d.requireAuth,
      mode: d.mode,
    };
    if (d.password) body.password = d.password;
    try {
      const r = editing
        ? await mailCall<{ account: Account }>("PATCH", `/${initial.id}`, body)
        : await mailCall<{ account: Account }>("POST", "", body);
      setError(null);
      onSaved(r.account);
    } catch (e) {
      setError(errorText((e as Error).message));
    }
  };
  return (
    <div className="flex flex-col gap-3" data-testid="mail-form">
      <p className="text-[12.5px] text-neutral-500">{t("mail.providerHint")}</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label={t("mail.name")}>
          <Input
            value={d.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder={t("mail.namePlaceholder")}
            data-testid="mail-name"
          />
        </Field>
        <Field label={t("mail.username")}>
          <Input
            value={d.username}
            onChange={(e) => set({ username: e.target.value })}
            autoComplete="off"
            data-testid="mail-username"
          />
        </Field>
        <Field label={t("mail.host")}>
          <Input
            value={d.host}
            onChange={(e) => set({ host: e.target.value })}
            placeholder="imap.example.org"
            data-testid="mail-host"
          />
        </Field>
        <Field label={t("mail.password")} hint={editing ? t("mail.passwordKept") : t("mail.passwordHint")}>
          <Input
            type="password"
            value={d.password ?? ""}
            onChange={(e) => set({ password: e.target.value })}
            autoComplete="new-password"
            data-testid="mail-password"
          />
        </Field>
        <Field label={t("mail.port")}>
          <Input
            inputMode="numeric"
            value={d.port ?? ""}
            onChange={(e) => set({ port: Number(e.target.value) || undefined })}
            data-testid="mail-port"
          />
        </Field>
        <Field label={t("mail.security")}>
          <Select
            value={d.security}
            onChange={(e) => set({ security: e.target.value as Account["security"] })}
            data-testid="mail-security"
          >
            {(["ssl", "starttls", "plain"] as const).map((s) => (
              <option key={s} value={s}>
                {t(`mail.securityOption.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("mail.folder")}>
          <Input
            value={d.folder}
            onChange={(e) => set({ folder: e.target.value })}
            data-testid="mail-folder"
          />
        </Field>
        <Field label={t("mail.doneFolder")} hint={t("mail.doneFolderHint")}>
          <Input
            value={d.doneFolder}
            onChange={(e) => set({ doneFolder: e.target.value })}
            data-testid="mail-done-folder"
          />
        </Field>
        <Field label={t("mail.workspace")}>
          <Select
            value={d.workspace}
            onChange={(e) => set({ workspace: e.target.value })}
            data-testid="mail-workspace"
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("mail.toLabel")}>
          <Select value={d.to} onChange={(e) => set({ to: e.target.value as Account["to"] })}>
            <option value="task">{t("mail.to.task")}</option>
            <option value="tray">{t("mail.to.tray")}</option>
          </Select>
        </Field>
      </div>
      <Field label={t("mail.allowed")} hint={t("mail.allowedHint")}>
        <Textarea
          rows={2}
          value={allowed}
          onChange={(e) => setAllowed(e.target.value)}
          data-testid="mail-allowed"
        />
      </Field>
      <label className="flex items-center gap-2 text-[13px]">
        <Checkbox
          checked={Boolean(d.requireAuth)}
          label={t("mail.requireAuth")}
          onChange={(v) => set({ requireAuth: v })}
        />
        {t("mail.requireAuth")}
      </label>
      <p className="-mt-2 text-[12px] text-neutral-500">{t("mail.requireAuthHint")}</p>
      <Field label={t("mail.mode")}>
        <Select
          value={d.mode}
          onChange={(e) => set({ mode: e.target.value as Account["mode"] })}
          className="w-fit"
        >
          <option value="first">{t("mail.modeFirst")}</option>
          <option value="all">{t("mail.modeAll")}</option>
        </Select>
      </Field>
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={() => void save()} data-testid="mail-save">
          {editing ? t("mail.saveAccount") : t("mail.addAccount")}
        </Button>
        {onCancel && (
          <Button variant="secondary" size="sm" onClick={onCancel}>
            {t("mail.cancel")}
          </Button>
        )}
      </div>
      {error && (
        <p className="text-[12.5px] text-err" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function outcomeText(
  r: TestResult,
  t: (k: string, o?: Record<string, unknown>) => string,
  spaceName: (id?: string) => string,
) {
  if (r.outcome === "left")
    return t(`mail.outcome.left.${r.reason}`, { defaultValue: t("mail.outcome.leftOther") });
  if (r.outcome === "ignored") return t("mail.outcome.ignored");
  if (r.outcome === "thread") return t("mail.outcome.thread");
  const a = r.actions ?? {};
  const bits = [
    a.group && t("mail.outcome.group", { name: a.group }),
    a.tags?.length && t("mail.outcome.tags", { names: a.tags.join(", ") }),
    a.contexts?.length && a.contexts.join(" "),
    a.priority && t(`mail.priority.${a.priority}`),
  ].filter(Boolean);
  const head = t(r.outcome === "tray" ? "mail.outcome.tray" : "mail.outcome.task", {
    space: spaceName(r.workspace),
  });
  return [head, ...bits].join(" · ");
}

function AccountRow({
  account,
  workspaces,
  people,
  onChange,
}: {
  account: Account;
  workspaces: { id: string; name: string }[];
  people: { id: string; label: string }[];
  onChange(a: Account | null): void;
}) {
  const { t, i18n } = useTranslation();
  const errorText = useErrorText();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [rules, setRules] = useState<Rule[]>(account.rules);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<TestResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const spaceName = (id?: string) => workspaces.find((w) => w.id === id)?.name ?? "";
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setError(null);
    } catch (e) {
      setError(errorText((e as Error).message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="py-2" data-testid="mail-row" data-name={account.name}>
      <button
        type="button"
        className="flex w-full flex-wrap items-center gap-2 text-left"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        data-testid="mail-open"
      >
        <span className="text-[13.5px] font-medium">{account.name}</span>
        <span className="text-[12.5px] text-neutral-500">{account.username}</span>
        <span className="ml-auto">
          {account.lastError ? (
            <StatusText tone="err" testId="mail-status">
              {errorText(account.lastError)}
            </StatusText>
          ) : (
            <StatusText tone="quiet" testId="mail-status">
              {account.lastPollAt
                ? t("mail.lastPoll", { when: fmt.format(new Date(account.lastPollAt)) })
                : t("mail.notYet")}
            </StatusText>
          )}
        </span>
      </button>
      {open && (
        <div className="mt-3 flex flex-col gap-4 border-l-2 border-neutral-200 pl-3 dark:border-neutral-800">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-[13px]">
              <Checkbox
                checked={account.enabled}
                label={t("mail.enabled")}
                onChange={(v) =>
                  void run(async () =>
                    onChange(
                      (await mailCall<{ account: Account }>("PATCH", `/${account.id}`, { enabled: v }))
                        .account,
                    ),
                  )
                }
              />
              {t("mail.enabled")}
            </label>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void run(async () =>
                  onChange((await mailCall<{ account: Account }>("POST", `/${account.id}/check`)).account),
                )
              }
              data-testid="mail-check"
            >
              {t("mail.check")}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(!editing)}>
              {t("mail.edit")}
            </Button>
            <ConfirmButton
              size="sm"
              variant="danger"
              question={t("mail.removeConfirm", { name: account.name })}
              onConfirm={() =>
                void run(async () => {
                  await mailCall("DELETE", `/${account.id}`);
                  onChange(null);
                })
              }
              testId="mail-remove"
            >
              {t("mail.remove")}
            </ConfirmButton>
          </div>
          {editing && (
            <AccountForm
              initial={account}
              workspaces={workspaces}
              onSaved={(a) => {
                setEditing(false);
                onChange(a);
              }}
              onCancel={() => setEditing(false)}
            />
          )}

          <div className="flex flex-col gap-2">
            <h4 className="text-[12.5px] font-semibold text-neutral-600 dark:text-neutral-300">
              {t("mail.rules")}
            </h4>
            <p className="text-[12px] text-neutral-500">
              {account.mode === "all" ? t("mail.modeAllHint") : t("mail.modeFirstHint")}
            </p>
            <RulesEditor
              rules={rules}
              workspaces={workspaces}
              people={people}
              onChange={(next) => {
                setRules(next);
                setDirty(true);
              }}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={!dirty}
                onClick={() =>
                  void run(async () => {
                    const r = await mailCall<{ account: Account }>("PATCH", `/${account.id}`, { rules });
                    setRules(r.account.rules);
                    setDirty(false);
                    onChange(r.account);
                  })
                }
                data-testid="rules-save"
              >
                {t("mail.saveRules")}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() =>
                  void run(async () =>
                    setResults(
                      (await mailCall<{ results: TestResult[] }>("POST", `/${account.id}/test`, { rules }))
                        .results,
                    ),
                  )
                }
                data-testid="mail-test"
              >
                {t("mail.test")}
              </Button>
            </div>
            {results && (
              <ul className="flex flex-col gap-1 text-[12.5px]" data-testid="mail-test-results">
                {results.length === 0 && <li className="text-neutral-500">{t("mail.testNone")}</li>}
                {results.map((r, i) => (
                  <li key={i} data-testid="mail-test-result">
                    <span className="font-medium">{r.subject || t("mail.noSubject")}</span>{" "}
                    <span className="text-neutral-500">· {r.from}</span>
                    <br />→ {outcomeText(r, t, spaceName)}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div data-testid="mail-left">
            <h4 className="mb-1 text-[12.5px] font-semibold text-neutral-600 dark:text-neutral-300">
              {t("mail.leftAlone")}
            </h4>
            {account.leftAlone.length === 0 ? (
              <p className="text-[12.5px] text-neutral-500">{t("mail.leftNone")}</p>
            ) : (
              <ul className="flex flex-col gap-0.5 text-[12.5px]">
                {account.leftAlone.map((x, i) => (
                  <li key={i}>
                    {x.from} · {x.subject || t("mail.noSubject")}{" "}
                    <span className="text-neutral-500">· {t(`mail.reason.${x.reason}`)}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-[12px] text-neutral-500">{t("mail.leftHint")}</p>
          </div>
          {error && (
            <p className="text-[12.5px] text-err" role="alert">
              {error}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Settings → Email in (I6, expert level): a person's own mail accounts. Mail sent or forwarded there becomes a
 * task or a tray item, sorted by routing rules; handled mail is moved to a folder, never deleted.
 */
export function MailInSettings() {
  const { t } = useTranslation();
  const session = useSession((s) => s.session);
  const { handle } = useCoreDoc();
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [adding, setAdding] = useState(false);
  const peopleJson = useDocSelector(handle, (doc) =>
    JSON.stringify(
      ((
        doc.getMap("propDefs").toJSON() as Record<
          string,
          { spec?: { values?: { id: string; label: string }[] } }
        >
      ).with?.spec?.values ?? []) as { id: string; label: string }[],
    ),
  );
  const people = JSON.parse(peopleJson ?? "[]") as { id: string; label: string }[];
  const workspaces = (session?.workspaces ?? [])
    .filter((w) => w.role !== "viewer")
    .map((w) => ({ id: w.id, name: w.name }));
  useEffect(() => {
    void mailCall<{ accounts: Account[] }>("GET", "")
      .then((b) => setAccounts(b.accounts))
      .catch(() => setAccounts([]));
  }, []);
  if (!session?.principal) return null;
  return (
    <SettingsSection
      id="settings-mail"
      intro={t("mail.hint")}
      summary={t("settings.summary.mail")}
      title={t("mail.title")}
      testId="settings-mail"
      folded
    >
      {accounts && accounts.length > 0 && (
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {accounts.map((a) => (
            <AccountRow
              key={a.id}
              account={a}
              workspaces={workspaces}
              people={people}
              onChange={(next) =>
                setAccounts(
                  next
                    ? accounts.map((x) => (x.id === a.id ? next : x))
                    : accounts.filter((x) => x.id !== a.id),
                )
              }
            />
          ))}
        </ul>
      )}
      {adding ? (
        <AccountForm
          initial={EMPTY}
          workspaces={workspaces}
          onSaved={(a) => {
            setAdding(false);
            setAccounts([...(accounts ?? []), a]);
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <Button
          variant="secondary"
          size="sm"
          className="w-fit"
          onClick={() => setAdding(true)}
          data-testid="mail-add"
        >
          {t("mail.add")}
        </Button>
      )}
    </SettingsSection>
  );
}
